import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MessageService } from "../../../../services/message.service";
import { MasterService } from "../../../../services/master.service";
import { StoreService } from "../../../../services/store.service";
import * as words from '../../../../services/language'
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs';

import { SEARCH_DEBOUNCE_MILLIS, MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { SecurityService } from "src/app/services/security.service";
import { Inventory } from "src/app/models/inventory.model";
import { GpsDevice } from "src/app/models/gps-device.model";
import { InventoryStatus } from "src/app/models/inventory-status.model";

@Component({
    selector: "gps-inventory-list",
    templateUrl: "./gps-inventory-list.component.html",
    styleUrls: ["./gps-inventory-list.component.scss"],
    standalone: false
})
export class GPSInventoryListComponent implements OnInit, OnChanges, OnDestroy {

    @Input() allowAddVehicle: boolean = false

    @Input() hasGPS: boolean = false

    @Input() refresh: boolean = false

    @Input() vehicleId: boolean = false

    @Output() deviceAssigned$: EventEmitter<Object> = new EventEmitter()

    // Let the inventory add component know the user wants to save the information.
    protected vehicleSavedEvent: Subject<Object> = new Subject();

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = words.language

    // save all data by type
    protected vehicles: Inventory[] = [];

    // define if is loading
    protected loading: boolean = false;

    // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number = 0

    // The search form group
    protected form: FormGroup = this.fb.group({
        inquiry: new FormControl<string>('', []),
        status: new FormControl<string>('', [])
    })

    protected gpsInventoryAssignmentInformation = new FormGroup({
        id: new FormControl<number>(0),
        gpsDeviceId: new FormControl<number | null>(null, [Validators.required]),
        vehicleId: new FormControl<number>(0, [Validators.required]),
        deviceLocation: new FormControl<string | null>(null),
        installedBy: new FormControl<string | null>(null)
    })

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'stockNumber': 'ASC' }

    private subscriptions: Subscription[] = []

    // The id of the current lot
    private lotId = 0

    protected canSave: boolean = false

    protected statuses: InventoryStatus[] = []

    protected selectedVehicleData: Inventory | null = null;

    protected gpsDevices: GpsDevice[] = []

    protected unassigned: GpsDevice[] = []

    // ************
    // life cycles
    // *************
    constructor(private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, protected store: StoreService, private security: SecurityService, private fb: FormBuilder) { }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot'))
        this.getInventoryStatuses()


        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.inquiry === inquiry) {
                    return
                }

                this.watcher = setTimeout(() => {
                    this.watcher = null
                    this.inquiry = inquiry
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('status').valueChanges.subscribe(status => {
                this.reset()
                this.fetch()
            })
        )
        
        this.reset()
        this.fetch()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes && changes.hasGPS) {
            this.reset()
            this.fetch()
        }

        if (changes && changes.refresh && changes.refresh.currentValue == true) {
            this.reset()
            this.fetch()
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    public update() {
        this.reset()
        this.fetch()
    }

    /**
     * Reset the pagination parameters and scroll to the top
     */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0
        this.vehicles = []
        this.lotId = this.lotId == 0 ? parseInt(localStorage.getItem('lot')) : this.lotId

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /*
    * functions
    */

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    protected sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        field = field.toLowerCase()
        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    protected sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        field = field.toLowerCase()
        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    protected sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        field = field.toLowerCase()
        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Acquire inventory list
     */
    protected fetch = (): void => {

        if (this.loading) { // already getting next page?
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        if (this.lotId == 0) {
            return
        }

        this.loading = true

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let statusParam = ''
        const status = parseInt(this.form.get('status').value)
        if (status && (!isNaN(status)) && (status > 0)) {
            statusParam = `&statuses=${status}`
        }

        let hasGPSParam = `&hasGPS=${this.hasGPS}`

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        this.master.get(`gpsInventory?lotId=${this.lotId}&page=${this.page}${statusParam}${inquiryParam}${sortParam}${hasGPSParam}`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.vehicles = this.vehicles.concat(res.data.inventory)
                return
            }

            this.vehicles = res.data.inventory

        })
    }

    // todo: listen to output of navbar
    protected listenerNav = (e): void => {

        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break;
            case 'changeType':
                this.form.get('inquiry').setValue('')
                this.reset()
                this.fetch()
                break
            default:
                break;
        }
    }

    protected getInventoryStatuses() {
        this.master.get(`collections/inventory/statuses`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.statuses = res.data.inventoryStatuses
        })
    }

    protected assignDevice = () => {
        let payload = this.gpsInventoryAssignmentInformation.value
        payload['status'] = 'Assigned'
        this.master.post('gpsInventoryAssignment', { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const btn = document.getElementById('assign-modal-btn-' + this.hasGPS)
            if (btn) { btn.click() }

            this.deviceAssigned$.emit({ message: 'refreshList' })

            this.reset()
            this.fetch()

        })
    }

    protected editDeviceAssignment = () => {
        let payload = this.gpsInventoryAssignmentInformation.value
        payload['status'] = 'Assigned'
        this.master.put(`gpsInventoryAssignment/${payload.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            const btnEdit = document.getElementById('edit-modal-btn-' + this.hasGPS)
            if (btnEdit) { btnEdit.click() }

            this.deviceAssigned$.emit({ message: 'refreshList' })

        })
    }

    protected selectDevice = (selectedDevice) => {
        this.gpsInventoryAssignmentInformation.patchValue({
            gpsDeviceId: selectedDevice.id
        })

        const elements = document.getElementsByClassName("device-list") as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < elements.length; i++) {
            elements[i].style.backgroundColor = "transparent";
        }

        (document.getElementById(selectedDevice.id) as HTMLElement).style.backgroundColor = '#d3e0d3'
    }

    protected removeGPSInventoryAssignment = (data) => {
        this.master.discard(`gpsInventoryAssignment/${data.id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.unassignDevice(data)
        })
    }

    protected unassignDevice = (data) => {
        let payload = { status: 'Unassigned', id: data.gpsDeviceId }
        this.master.put(`gpsDevice/${data.gpsDeviceId}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }


        })
    }


    protected activateDevice = (data) => {
        let payload = { status: 'Activated', vehicleId: data.vehicleId }
        this.master.put(`gpsDevice/${data.gpsDeviceId}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }


        })
    }

    protected deactivateDevice = (data) => {
        let payload = { status: 'Inactive', inventoryAssignmentId: data.id }
        this.master.put(`gpsDevice/${data.gpsDeviceId}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }


        })
    }

    protected resetForm = () => {
        this.gpsInventoryAssignmentInformation.reset()
    }

    protected assignSetup = (data) => {
        this.selectedVehicleData = data
        this.listGPSDevices()
        this.gpsInventoryAssignmentInformation.patchValue({
            vehicleId: data.vehicleId,
        })
        const btn = document.getElementById('assign-modal-btn-' + this.hasGPS)
        if (btn) btn.click()
    }

    protected editAssignmentSetup = (selectedDevice, selectedVehicle) => {
        this.selectedVehicleData = selectedVehicle
        this.gpsInventoryAssignmentInformation.patchValue({
            id: selectedDevice.id,
            gpsDeviceId: selectedDevice.gpsDeviceId,
            vehicleId: selectedVehicle.vehicleId,
            deviceLocation: selectedDevice.deviceLocation,
            installedBy: selectedDevice.installedBy
        })
        const btn = document.getElementById('edit-modal-btn-' + this.hasGPS)
        if (btn) btn.click()
    }

    protected removeAssignmentSetup = (selectedDevice, selectedVehicle) => {
        //TODO: Implement this, added because it was missing (dml)
    }

    protected listGPSDevices = () => {
        this.master.get('gpsDevices', res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.gpsDevices = res.data.gpsDevices
            this.unassigned = this.gpsDevices.length > 0 ? this.gpsDevices.filter(gpsDevice => gpsDevice['status'] == 'Unassigned') : null
        })
    }

    protected listGPSDevicesForVehicle = (vehicleId) => {
        const found = this.vehicles.findIndex(res => res['vehicleId'] == vehicleId)
        if (((found > -1 && this.vehicles[found]['devices'] !== null && this.vehicles[found]['devices'].length > 0)) || found == -1) {
            return
        }
        this.loading = true
        this.master.get(`gpsInventoryAssignments?vehicleId=${vehicleId}`, res => {
            this.loading = false
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            let vehicleGPSDevices = res.data.GPSInventoryAssignment
            for (const item of vehicleGPSDevices) {
                this.vehicles[found]['devices'].push({
                    gpsDeviceId: item.gpsDeviceId,
                    productName: item.gpsDevice.productName,
                    integrationCompanyName: item.gpsDevice.integrationCompany.name,
                    serialNumber: item.gpsDevice.serialNumber,
                    gpsDeviceStatus: item.gpsDevice.status,
                    installedBy: item.installedBy,
                    deviceLocation: item.deviceLocation,
                    id: item.id
                })
            }
        })
    }

}
